import React, { useState, useEffect } from 'react';
import { connectAutoComplete } from 'react-instantsearch-dom';
import { PropTypes } from 'prop-types';
import styled from 'styled-components';

import MagnifyingGlassIcon from '@icons/MagnifyingGlassIcon';
import TextInput from '@atoms/TextInput';
import colors from '@general/colors';

const List = styled.ul`
    list-style: none;
    position: absolute;
    left: 0;
    top: ${props => (props.hasLabel ? '95px' : '65px')};
    z-index: 1;
    width: 100%;
    max-height: 390px;
    overflow: scroll;
    margin: 0;
    padding: 0;
    border: 1px solid #d9d9d9;
    border-radius: 5px;
    background-color: #ffffff;
    box-shadow: 0 0 16px 0 rgba(87, 87, 87, 0.2);
`;

const ListItem = styled.li`
    min-height: ${props => (props.listItemMinHeight ? props.listItemMinHeight : 'auto')};
    padding: 10px 10px;
    cursor: pointer;
    display: flex;
    align-items: center;
    &.browsed-option,
    &:hover {
        background-color: #deebff;
    }
`;

const SubmitButtonStyled = styled.button`
    position: absolute;
    bottom: 0;
    right: 0;
    height: 54px;
    width: 54px;
    background-color: ${colors.azure};
`;

const Autocomplete = props => {
    const {
        name,
        label,
        hits,
        currentRefinement,
        refine,
        compHit,
        placeholder,
        onSelect,
        initialRefinement = '',
        listItemMinHeight,
    } = props;
    const [showList, setShowList] = useState(true);
    const [browsedHit, setBrowsedHit] = useState();
    const [hasChanged, setHasChanged] = useState(false);
    const Hit = compHit;

    useEffect(() => {
        setBrowsedHit(hits[0]);
    }, [hits]);

    const handleSelect = hit => {
        refine(hit.name);
        setShowList(false);
        if (onSelect) {
            onSelect(hit);
        }
    };

    const onChange = e => {
        setHasChanged(true);
        refine(e.currentTarget.value);
        setShowList(true);
    };

    const onKeyDown = e => {
        // down
        if (e.keyCode === 40) {
            if (browsedHit && hits.length > browsedHit.__position) {
                setBrowsedHit(hits[browsedHit.__position]);
            }
        }
        // up
        if (e.keyCode === 38) {
            if (browsedHit && browsedHit.__position - 2 >= 0) {
                setBrowsedHit(hits[browsedHit.__position - 2]);
            }
        }
        // return
        if (e.keyCode === 13) {
            e.preventDefault();
            if (browsedHit) {
                handleSelect(browsedHit);
            }
        }
    };

    return (
        <div style={{ position: 'relative' }}>
            <div style={{ position: 'relative' }}>
                <TextInput
                    autoComplete="off"
                    label={label}
                    name={name}
                    type="search"
                    value={!hasChanged ? initialRefinement : currentRefinement}
                    onChange={onChange}
                    onKeyDown={onKeyDown}
                    placeholder={placeholder}
                />
                <SubmitButtonStyled type="submit" title="Submit your search query." className="ais-SearchBox-submit">
                    <MagnifyingGlassIcon size={20} />
                </SubmitButtonStyled>
                {showList && currentRefinement !== '' && (
                    <List hasLabel={!!label}>
                        {hits.length === 0 && <ListItem>No matches found</ListItem>}
                        {hits.map(hit => (
                            <ListItem
                                key={hit.objectID}
                                onClick={() => handleSelect(hit)}
                                className={browsedHit?.objectID === hit.objectID ? 'browsed-option' : ''}
                                listItemMinHeight={listItemMinHeight}
                            >
                                <Hit hit={hit} />
                            </ListItem>
                        ))}
                    </List>
                )}
            </div>
        </div>
    );
};

Autocomplete.propTypes = {
    label: PropTypes.string,
    name: PropTypes.string,
    hits: PropTypes.array,
    currentRefinement: PropTypes.any,
    refine: PropTypes.any,
    compHit: PropTypes.func,
    onSelect: PropTypes.func,
    placeholder: PropTypes.string,
    initialRefinement: PropTypes.string,
    listItemMinHeight: PropTypes.string,
};

export default connectAutoComplete(Autocomplete);
